import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { BackendComment } from "components/ContractsSettings/ContractEditorLexical/CommentPlugin/commenting";
import { downloadFile, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { SignatureProviderKey } from "config/signatureProviders";
import type { MainPortalType } from "./settingsStore";
import type { TFetchStatus } from "./types";

export type SignatureProvider = SignatureProviderKey;

export enum AgreementSignatureStatus {
    TO_SIGN = 0,
    SENT = 1,
    SIGNED = 2,
    REFUSED = 3,
}

export enum AgreementStatus {
    TRASHED = "trashed",
    DRAFT = "draft",
    NEGOTIATING = "negotiating",
    SENT = "sent",
    SIGNED = "signed",
}

export enum AgValidatorStatus {
    APPROVE = "approve",
    DECLINE = "decline",
    IDLE = "idle",
    WAITING = "waiting",
}

export interface AgreementValidator {
    firstname: string;
    lastname: string;
    agreementId: number;
    order: number;
    userId: number;
    status: AgValidatorStatus;
    modifiedAt: string;
    createdAt: string;
}

export interface Agreement {
    beneficiaryId: number;
    beneficiary: string;
    created: Date;
    filename: string;
    html: string;
    blocks?: string;
    emittedBy: number;
    id: number;
    lastUpdate: string;
    relatedResource: string;
    relatedResourceId: number;
    relatedResourceName: string;
    signedStatus: number;
    status: AgreementStatus;
    storageId: string;
    templateId?: number;
    userVariables?: Record<string, any>;
    signers: AgreementSigner[] | number;
    // Beneficiaries signing the agreement (list endpoint only); includes co-beneficiaries of a multi-signature
    beneficiarySignerIds?: number[];
    publisherSignerId: number;
    resentAt: Date | null;
    titleAvatarUrl: string | null;
    legacyTermTitleEan13: string | null;
    titleId: number | null;
    workId?: number | null;
    isRelatedResourceDeleted: boolean;
    validators: AgreementValidator[];
    mySignedStatus?: AgreementSignatureStatus | null;
    mySignatureUrl?: string | null;
    signProvider?: "crealo_sign" | "docoon";
    docoonLastCheckedAt?: string | null;
    docoonContractId?: number | null;
    type: "agreement" | "avenant";
    publicId?: string | null;
}

export type DocoonEvent = {
    id: number;
    docoonEventId: number;
    type: string | null;
    signataire: string | null;
    eventAt: string | null;
    ip: string | null;
    refusalComment?: string | null;
};

export type DocoonEventsData = {
    docoonContractId: number | null;
    docoonLastCheckedAt: string | null;
    createdAt: string;
    downloadStatus: string | null;
    events: DocoonEvent[];
    // Informed people (notified once signed): their name appears in events but they never sign
    informed?: string[];
};

export interface AgreementSigner {
    id: number;
    agreementId: number;
    relatedResource: "user" | "beneficiary";
    relatedResourceId: number;
    order: number;
    signedStatus: AgreementSignatureStatus;
    signedAt: Date | null;
    name: string;
    firstname: string;
    lastname: string;
    avatarUrl: string;
    email: string;
    phone?: string | null;
    socialSecurityNumber?: string | null;
    siretNumber?: string | null;
    hasPortal: number;
    signatureUrl?: string | null;
}

export interface AgreementTemplate {
    id: number;
    companyId?: number;
    name: string;
    // content: string | AgreementTemplateContent;
    blocks: Record<string, any>;
    html: string;
    isDeleted?: boolean;
    created: Date | string;
    modified: Date | string;
    validators: AgreementValidator[];
    type: "agreement" | "avenant" | "framework_contract";
}

export interface BeneficiaryAgreement {
    id?: number;
    companyId?: number;
    beneficiaryId: number;
    signedStatus: AgreementSignatureStatus;
    storageId: string;
    filename: string;
    htmlContent: string;
    relatedResource: string;
    relatedResourceId: number;
    created: Date | string;
    lastUpdate: Date | string;
    /**
     * Used in response, when downloading agreement
     */
    file?: any;
}

export type AgreementDocument = {
    agreementId: number;
    companyId: number;
    id: number;
    mimetype: string;
    name: string;
    size: string;
    storageId: string;
};

export const fetchAgreements = createAsyncThunk("agreement/fetchAgreements", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/agreements`);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export type EligibleAgreementResource = {
    id: number;
    type: "term" | "article" | "framework_contract";
    name: string;
    beneficiaryName: string;
    beneficiaryId: number;
    titleId: number | null;
    // Book terms: the work, whose terms are the possible co-signers
    workId?: number | null;
    titleAvatarUrl: string | null;
    email: string | null;
};

export const fetchEligibleResourcesForAgreement = createAsyncThunk(
    "agreement/fetchEligibleResources",
    async (
        {
            documentType,
            search,
            limit,
        }: { documentType: "agreement" | "avenant" | "framework_contract"; search?: string; limit?: number },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/eligible-resources`,
                {
                    params: {
                        type: documentType,
                        ...(search ? { search } : {}),
                        limit: limit ?? 20,
                    },
                },
            );
            return (response.data?.data ?? []) as EligibleAgreementResource[];
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

export const fetchAgreement = createAsyncThunk(
    "agreement/fetchAgreement",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

/**
 * .patch() is used for update an agreement or send it for validation or signature as well
 * @param behavior putAgremeent call behavior, update a separate state for send agreement fullfilled (validation/signature)
 */
export const putAgreement = createAsyncThunk(
    "agreement/putAgreement",
    async (
        { data, id }: { data: any; id: number; behavior?: "send_for_signature_or_validation" },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.patch(import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${id}`, data);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteAgreement = createAsyncThunk(
    "agreement/deleteAgreement",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAgreementPreview = createAsyncThunk(
    "agreement/fetchAgreementPreview",
    async ({ agreementId, filename }: { agreementId: number; filename: string }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/preview`, {
                    params: { download: true },
                }),
                {
                    pending: `${t("Articles.ContractPending")}`,
                    success: `${t("Articles.ContractSucceeded")}`,
                    error: `${t("Articles.ContractFailed")}`,
                },
            );
            const linkSource = `data:application/pdf;base64,${response.data}`;
            downloadFile({ source: linkSource, fileName: filename, isLink: true });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAgreementPDF = createAsyncThunk(
    "agreement/fetchAgreementPDF",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/preview`,
                { params: { download: true } },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const approveAgreement = createAsyncThunk(
    "agreement/approveAgreement",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/approve`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export type DocoonSignatory = {
    id: number;
    lastName: string;
    firstName: string;
    email: string;
    phone?: string | null;
    modeSecure?: string | null;
    identType?: string | null;
    identCode?: string | null;
};

/** A picked signatory and the block it was picked in. The first one of a list is the main signatory. */
export type DocoonSignatoryWithRole = DocoonSignatory & {
    // Co-beneficiary of a multi-signature contract this signatory signs for
    beneficiaryId?: number | null;
    isInternal?: boolean;
    // Notified once the contract is signed, does not sign
    isInformed?: boolean;
};

export type DocoonSignatoryPayload = {
    docoonId: number;
    firstName: string;
    lastName: string;
    isMainSignatory: boolean;
    beneficiaryId?: number | null;
    isInternalSignatory?: boolean;
    isInformed?: boolean;
};

export const toDocoonSignatoriesPayload = (signatories: DocoonSignatoryWithRole[]): DocoonSignatoryPayload[] =>
    signatories.map((s, i) => ({
        docoonId: s.id,
        firstName: s.firstName,
        lastName: s.lastName,
        isMainSignatory: i === 0,
        beneficiaryId: s.beneficiaryId ?? null,
        isInternalSignatory: !!s.isInternal,
        isInformed: !!s.isInformed,
    }));

export const fetchDocoonSignatories = createAsyncThunk(
    "agreement/fetchDocoonSignatories",
    async (
        arg: {
            email?: string;
            firstName?: string;
            lastName?: string;
            titleId?: number;
            mainEmail?: string;
            signal?: AbortSignal;
        } = {},
        { rejectWithValue },
    ) => {
        const { signal, ...params } = arg;
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/integrations/docoon/signatories`,
                { params, signal },
            );
            return response.data.data as DocoonSignatory[];
        } catch (error: any) {
            if (axios.isCancel(error)) return rejectWithValue({ canceled: true });
            return rejectWithValue(error.response?.data);
        }
    },
);

export const fetchAgreementDocoonSignatories = createAsyncThunk(
    "agreement/fetchAgreementDocoonSignatories",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/docoon-signatories`,
            );
            return response.data.data as {
                docoonId: number;
                firstName: string;
                lastName: string;
                isMainSignatory: number;
                beneficiaryId: number | null;
                isInternalSignatory: number;
                isInformed: number;
            }[];
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const saveAgreementDocoonSignatories = createAsyncThunk(
    "agreement/saveAgreementDocoonSignatories",
    async (
        {
            agreementId,
            signatories,
        }: {
            agreementId: number;
            signatories: DocoonSignatoryPayload[];
        },
        { rejectWithValue },
    ) => {
        try {
            await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/docoon-signatories`, {
                signatories,
            });
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const sendDocoonAgreement = createAsyncThunk(
    "agreement/sendDocoonAgreement",
    async (
        {
            agreementId,
            signatairesIds,
            signatureTemplate,
            signSequential,
            serviceEdc,
            typeDocEdc,
            comment,
        }: {
            agreementId: number;
            signatairesIds: string;
            signatureTemplate?: string;
            signSequential?: number;
            serviceEdc?: string;
            typeDocEdc?: string;
            comment?: string;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/send-to-docoon`,
                { signatairesIds, signatureTemplate, signSequential, serviceEdc, typeDocEdc, comment },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const forceSendAgreement = createAsyncThunk(
    "agreement/forceSendAgreement",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/send-force`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const declineAgreement = createAsyncThunk(
    "agreement/declineAgreement",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/decline`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAgreementValidators = createAsyncThunk(
    "agreement/fetchAgreementValidators",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/validators`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAgreementsComment = createAsyncThunk(
    "agreement/fetchAgreementsComment",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/comments`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const postAgreementsComment = createAsyncThunk(
    "agreement/postAgreementsComment",
    async ({ data, id }: { data: any; id: number }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${id}/comments`,
                data,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteAgreementsComment = createAsyncThunk(
    "agreement/deleteAgreementComment",
    async ({ agreementId, commentId }: { agreementId: number; commentId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/comments/${commentId}`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAgreementsTemplate = createAsyncThunk(
    "agreement/fetchAgreementsTemplate",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/templates`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAgreementTemplate = createAsyncThunk(
    "agreement/fetchAgreementTemplate",
    async (agreementId: number, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/templates/${agreementId}`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const postAgreementTemplate = createAsyncThunk(
    "agreement/postAgreementTemplate",
    async ({ data }: { data: any }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/templates`, data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putAgreementTemplate = createAsyncThunk(
    "agreement/putAgreementTemplate",
    async ({ data, id }: { data: any; id: number }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/templates/${id}`,
                data,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteAgreementTemplate = createAsyncThunk(
    "agreement/deleteAgreementTemplate",
    async ({ id }: { id: number }, { rejectWithValue }) => {
        try {
            await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/templates/${id}`);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const putAgreementPublisherSigner = createAsyncThunk(
    "agreement/putAgreementPublisherSigner",
    async (
        { agreementId, publisherSignerId }: { agreementId: number; publisherSignerId: number },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/publisher-signer`,
                { publisherSignerId },
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAgreementGenerated = createAsyncThunk(
    "agreement/fetchAgreementGenerated",
    async (
        {
            elementId,
            titleId,
            templateId,
            from,
        }: {
            elementId: number;
            titleId?: number | null;
            templateId: number;
            from: MainPortalType;
        },
        { rejectWithValue },
    ) => {
        const link = ["audiovisual", "press"].includes(from)
            ? `/title/${titleId}/article/${elementId}/agreement`
            : `/term/${elementId}/agreement`;
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + link, { params: { templateId } });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const postAgreement = createAsyncThunk(
    "agreement/postAgreement",
    async (
        {
            relatedResourceId,
            relatedResource,
            templateId,
            userVariables,
            html,
            blocks,
            publisherSignerId,
            beneficiaryIds,
            signatureProvider,
            type,
            docoon,
        }: {
            relatedResourceId: number;
            relatedResource: "term" | "article" | "frameworkContract";
            templateId: number;
            userVariables?: any;
            html?: string;
            blocks?: string;
            publisherSignerId?: number;
            beneficiaryIds?: number[];
            signatureProvider?: SignatureProvider;
            type?: "agreement" | "avenant";
            docoon?: {
                signatories?: DocoonSignatoryPayload[];
                signSequential?: number;
                serviceEdc?: string;
                typeDocEdc?: string;
                signatureTemplate?: string;
                comment?: string;
            };
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/agreements", {
                relatedResource,
                relatedResourceId,
                templateId,
                userVariables,
                html,
                blocks,
                publisherSignerId,
                beneficiaryIds,
                signatureProvider,
                type,
                docoon,
            });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const patchContractGenerated = createAsyncThunk(
    "agreement/patchContractGenerated",
    async (
        {
            agreementId,
            userVariables,
            html,
            blocks,
        }: {
            agreementId: number;
            userVariables: any;
            html: string;
            blocks: string;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.patch(import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}`, {
                sendContract: true,
                userVariables,
                html,
                blocks,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAgreementsValidators = createAsyncThunk(
    "agreement/fetchAgreementsValidators",
    async ({ id }: { id: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/templates/${id}/validators`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

type PutValidatorsReqType = "change-order" | "add-validator" | "remove-validator";

export const putValidatorsToAgreement = createAsyncThunk(
    "agreement/putValidatorsToAgreement",
    async (
        { id, validators, type }: { id: number; validators: any[]; type: PutValidatorsReqType },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/templates/${id}/validators`,
                validators,
            );
            return { data: response.data.data, type, agreementId: id };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const sendAgreementsInBulk = createAsyncThunk(
    "articles/sendAgreementsInBulk",
    async (
        {
            templateId,
            relatedResource,
            resourceIds,
            userVariables,
        }: {
            templateId: number;
            relatedResource: "term" | "article";
            resourceIds: number[];
            userVariables?: any;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/templates/${templateId}/send/bulk`,
                { relatedResource, resourceIds, userVariables },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const resendAgreement = createAsyncThunk(
    "agreement/resendAgreement",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/templates/${agreementId}/resend`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAgreementDocuments = createAsyncThunk(
    "agreement/fetchAgreementDocuments",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/documents`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const downloadAgreementDocument = createAsyncThunk(
    "agreement/downloadAgreementDocument",
    async (
        { agreementId, documentId, file }: { agreementId: number; documentId: number; file: AgreementDocument },
        { rejectWithValue },
    ) => {
        try {
            const response = await toast.promise(
                axios.get(
                    import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/documents/${documentId}`,
                    {
                        params: { download: true },
                    },
                ),

                {
                    pending: `${t("AddDocument.DownloadDocumentPending")}`,
                    success: `${t("AddDocument.DownloadDocumentSuccess")}`,
                    error: `${t("AddDocument.DownloadDocumentError")}`,
                },
            );
            const linkSource = `data:${file.mimetype ?? "text/plain"};base64,${response.data}`;
            const downloadLink = document.createElement("a");
            const fileName = file.name;
            downloadLink.href = linkSource;
            downloadLink.download = fileName;
            downloadLink.click();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const postAgreementDocument = createAsyncThunk(
    "agreement/postAgreementDocument",
    async ({ agreementId, formData }: { agreementId: number; formData: FormData }, { rejectWithValue }) => {
        try {
            const config = {
                headers: {
                    "content-type": "multipart/form-data",
                },
            };
            const response = await toast.promise(
                axios.post(
                    import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/documents`,
                    formData,
                    config,
                ),
                {
                    pending: `${t("AddDocument.SendDocumentPending")}`,
                    success: `${t("AddDocument.SendDocumentSuccess")}`,
                    error: `${t("AddDocument.SendDocumentError")}`,
                },
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteAgreementDocument = createAsyncThunk(
    "agreement/deleteAgreementDocument",
    async ({ agreementId, documentId }: { agreementId: number; documentId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/documents/${documentId}`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAgreementSigners = createAsyncThunk(
    "agreement/fetchAgreementSigners",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/signers`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchDocoonEvents = createAsyncThunk(
    "agreement/fetchDocoonEvents",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/docoon-events`,
            );
            return response.data.data as DocoonEventsData;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const syncDocoonEvents = createAsyncThunk(
    "agreement/syncDocoonEvents",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/docoon-events/sync`,
            );
            return response.data.data as DocoonEventsData;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const resendToDocoon = createAsyncThunk(
    "agreement/resendToDocoon",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/resend-to-docoon`,
            );
            return response.data.data as { newAgreementId: number };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const revertAgreementToDraft = createAsyncThunk(
    "agreement/revertAgreementToDraft",
    async ({ agreementId }: { agreementId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/revert-to-draft`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

const allFetchStatuses = [
    "agreementStatus",
    "agreementsStatus",
    "agreementEditStatus",
    "deleteAgreementStatus",
    "agreementsTemplateStatus",
    "agreementTemplateStatus",
    "agreementCommentsStatus",
    "agreementSignersStatus",
    "sendAgreementStatus",
    "resendAgreementStatus",
    "agreementPDFStatus",
    "agreementDocumentsStatus",
    "revertToDraftStatus",
];

export type IAgFetchStatus = Record<(typeof allFetchStatuses)[number], TFetchStatus>;

const initialAgFetchStatus = Object.fromEntries(allFetchStatuses.map((status) => [status, "idle"])) as IAgFetchStatus;

export interface AgreementState {
    agreement: Agreement | null;
    agreements: Agreement[];
    agreementsTemplate: AgreementTemplate[];
    agreementTemplate: AgreementTemplate | null;
    agreementGenerated: any;
    agreementsGenerated: string[] | null;
    agreementValidators: AgreementValidator[];
    agreementsComments: BackendComment[] | null;
    defaultAgreementTemplate: AgreementTemplate | null;
    savedEditorContent: { stringifiedBlocks: string; html: string };
    agreementPDF: string;
    agreementDocuments: AgreementDocument[];
    agreementSigners: AgreementSigner[];
    navigateAfterSendingAg: boolean;
    agFetchStatus: IAgFetchStatus;
    docoonEvents: DocoonEventsData | null;
    docoonEventsFetchStatus: TFetchStatus;
    docoonEventsSyncStatus: TFetchStatus;
}

const initialState: AgreementState = {
    agreement: null,
    agreements: [],
    agreementsTemplate: [],
    agreementTemplate: null,
    agreementGenerated: null,
    agreementsGenerated: null,
    agreementValidators: [],
    agreementsComments: null,
    defaultAgreementTemplate: null,
    savedEditorContent: { stringifiedBlocks: "", html: "" },
    agreementPDF: "",
    agreementDocuments: [],
    agreementSigners: [],
    /**
     * This state call a navigate("/agreements")
     * after sending ag for signature or validation
     */
    navigateAfterSendingAg: false,
    agFetchStatus: initialAgFetchStatus,
    docoonEvents: null,
    docoonEventsFetchStatus: "idle",
    docoonEventsSyncStatus: "idle",
};

/**
 * Update the fetch status for a specific key
 * @param key keyof IAgFetchStatus
 * @param value TFetchStatus
 * @returns Action
 */
export const updateAgFetchStatus = (key: keyof IAgFetchStatus, value: TFetchStatus) => {
    return setAgFetchStatus({ key, value });
};

export const agreementsSlice = createSlice({
    name: "agreements",
    initialState,
    reducers: {
        resetAgreementsTemplateStatus(state) {
            state.agFetchStatus.agreementsTemplateStatus = "idle";
        },
        resetAgreementStatus(state) {
            state.agFetchStatus.agreementStatus = "idle";
        },
        resetAgreementCommentsStatus(state) {
            state.agFetchStatus.agreementCommentsStatus = "idle";
        },
        setSavedEditorContent(state, action) {
            state.savedEditorContent = action.payload;
        },
        resetAgreementPDFStatus(state) {
            state.agFetchStatus.agreementPDFStatus = "idle";
        },
        resetNavigateAfterSendingAg(state) {
            state.navigateAfterSendingAg = false;
        },
        resetAgreement(state) {
            state.agreement = null;
        },
        resetDocoonEvents(state) {
            state.docoonEvents = null;
            state.docoonEventsFetchStatus = "idle";
            state.docoonEventsSyncStatus = "idle";
        },
        /**
         * Update the fetch status for a specific key
         * @param state Redux Agreements state
         * @param action { payload: { key: keyof IAgFetchStatus; value: TFetchStatus } }
         */
        setAgFetchStatus(state, action: { payload: { key: keyof IAgFetchStatus; value: TFetchStatus } }) {
            state.agFetchStatus[action.payload.key] = action.payload.value;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchAgreements.pending, (state, _action) => {
            state.agFetchStatus.agreementsStatus = "pending";
        });
        builder.addCase(fetchAgreements.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.agFetchStatus.agreementsStatus = "failed";
        });
        builder.addCase(fetchAgreements.fulfilled, (state, action) => {
            state.agreements = action.payload.data;
            state.agFetchStatus.agreementsStatus = "succeeded";
        });

        builder.addCase(fetchAgreement.rejected, (state, { payload }) => {
            state.agFetchStatus.agreementStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchAgreement.pending, (state) => {
            state.agFetchStatus.agreementStatus = "pending";
        });
        builder.addCase(fetchAgreement.fulfilled, (state, action) => {
            state.agreement = action.payload.data;
            state.agFetchStatus.agreementStatus = "succeeded";
        });
        builder.addCase(putAgreement.pending, (state) => {
            state.agFetchStatus.agreementEditStatus = "pending";
        });
        builder.addCase(putAgreement.fulfilled, (state, action) => {
            const { behavior } = action.meta.arg;
            if (behavior == "send_for_signature_or_validation") {
                state.navigateAfterSendingAg = true;
            }
            state.agreement = action.payload;
            state.agFetchStatus.agreementEditStatus = "succeeded";
        });
        builder.addCase(putAgreement.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            state.agFetchStatus.agreementEditStatus = "failed";
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(deleteAgreement.pending, (state, _action) => {
            state.agFetchStatus.deleteAgreementStatus = "pending";
        });
        builder.addCase(deleteAgreement.fulfilled, (state, _action) => {
            state.agFetchStatus.deleteAgreementStatus = "succeeded";
        });
        builder.addCase(deleteAgreement.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            state.agFetchStatus.deleteAgreementStatus = "failed";
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchAgreementValidators.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchAgreementValidators.fulfilled, (state, action) => {
            state.agreementValidators = action.payload.data;
        });
        builder.addCase(fetchAgreementsComment.pending, (state, _action) => {
            state.agFetchStatus.agreementCommentsStatus = "pending";
        });
        builder.addCase(fetchAgreementsComment.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.agFetchStatus.agreementCommentsStatus = "failed";
        });
        builder.addCase(fetchAgreementsComment.fulfilled, (state, action) => {
            state.agreementsComments = action.payload.data;
            state.agFetchStatus.agreementCommentsStatus = "succeeded";
        });
        builder.addCase(fetchAgreementTemplate.fulfilled, (state, action) => {
            state.agreementTemplate = action.payload;
        });
        builder.addCase(fetchAgreementTemplate.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.agFetchStatus.agreementsTemplateStatus = "failed";
        });
        builder.addCase(fetchAgreementsTemplate.pending, (state, _action) => {
            state.agFetchStatus.agreementsTemplateStatus = "pending";
        });
        builder.addCase(fetchAgreementsTemplate.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.agFetchStatus.agreementsTemplateStatus = "failed";
        });
        builder.addCase(fetchAgreementsTemplate.fulfilled, (state, action) => {
            state.defaultAgreementTemplate = action.payload.data.find(
                (agreement: any) => agreement.name === "Modèle par défaut",
            );
            state.agreementsTemplate = action.payload.data.filter(
                (agreement: any) => agreement.name !== "Modèle par défaut",
            );
            state.agFetchStatus.agreementsTemplateStatus = "succeeded";
        });

        builder.addCase(postAgreementTemplate.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(postAgreementTemplate.fulfilled, (state, action) => {
            state.agreementsTemplate = [action.payload.data, ...state.agreementsTemplate];
            toast.success(`${t("ContractSettings.AddAgreementSuccess")}`);
        });

        builder.addCase(putAgreementTemplate.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(putAgreementTemplate.fulfilled, (state, action) => {
            const { data: agreement } = action.payload;
            state.agreementsTemplate = state.agreementsTemplate.map((agTemplate) => {
                if (agTemplate.id == agreement.id)
                    return { ...agTemplate, ...agreement, validators: agTemplate.validators };
                return agTemplate;
            });
            toast.success(`${t("ContractSettings.EditAgreementSuccess")}`);
        });

        builder.addCase(deleteAgreementTemplate.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(deleteAgreementTemplate.fulfilled, (state, action) => {
            state.agreementsTemplate = state.agreementsTemplate.filter((agreement) => agreement.id != action.payload);
            toast.success(`${t("ContractSettings.DeleteAgreementSuccess")}`);
        });

        builder.addCase(fetchAgreementGenerated.fulfilled, (state, action) => {
            state.agreementGenerated = action.payload;
            state.agFetchStatus.sendAgreementStatus = "succeeded";
        });
        builder.addCase(fetchAgreementGenerated.pending, (state) => {
            state.agFetchStatus.sendAgreementStatus = "pending";
        });
        builder.addCase(fetchAgreementGenerated.rejected, (state, { payload }) => {
            state.agFetchStatus.sendAgreementStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(postAgreement.fulfilled, (state, action) => {
            state.agreementGenerated = action.payload;
            state.agFetchStatus.sendAgreementStatus = "succeeded";
        });
        builder.addCase(postAgreement.pending, (state) => {
            state.agFetchStatus.sendAgreementStatus = "pending";
        });
        builder.addCase(postAgreement.rejected, (state, { payload }) => {
            state.agFetchStatus.sendAgreementStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(sendAgreementsInBulk.fulfilled, (state) => {
            state.agFetchStatus.sendAgreementStatus = "succeeded";
            toast.success(`${t("Agreements.ContractSentInBulkSuccess")}`);
        });
        builder.addCase(sendAgreementsInBulk.pending, (state) => {
            state.agFetchStatus.sendAgreementStatus = "pending";
        });
        builder.addCase(sendAgreementsInBulk.rejected, (state, { payload }) => {
            state.agFetchStatus.sendAgreementStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchAgreementsValidators.fulfilled, (state, action) => {
            state.agreementValidators = action.payload.data;
        });
        builder.addCase(fetchAgreementsValidators.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(putValidatorsToAgreement.fulfilled, (state, action) => {
            const { data: validators, type, agreementId } = action.payload;
            switch (type) {
                case "add-validator":
                    toast.success(`${t("ContractSettings.AddValidatorSuccess")}`);
                    break;
                case "remove-validator":
                    toast.success(`${t("ContractSettings.RemoveValidatorSuccess")}`);
            }
            state.agreementValidators = validators;
            state.agreementsTemplate = state.agreementsTemplate.map((agreement) => {
                if (agreement.id == agreementId) {
                    return { ...agreement, validators };
                }
                return agreement;
            });
        });
        builder.addCase(putValidatorsToAgreement.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(resendAgreement.fulfilled, (state) => {
            toast.success(`${t("Articles.SignatureSuccess")}`);
            state.agFetchStatus.resendAgreementStatus = "succeeded";
            state.agreement = {
                ...state.agreement,
                resentAt: new Date(),
            } as Agreement;
        });
        builder.addCase(resendAgreement.rejected, (state, { payload }) => {
            state.agFetchStatus.resendAgreementStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchAgreementPDF.pending, (state, _action) => {
            state.agFetchStatus.agreementPDFStatus = "pending";
        });
        builder.addCase(fetchAgreementPDF.fulfilled, (state, action) => {
            state.agreementPDF = action.payload;
            state.agFetchStatus.agreementPDFStatus = "succeeded";
        });
        builder.addCase(fetchAgreementPDF.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.agFetchStatus.agreementPDFStatus = "failed";
        });
        builder.addCase(fetchAgreementDocuments.pending, (state) => {
            state.agFetchStatus.agreementDocumentsStatus = "pending";
        });
        builder.addCase(fetchAgreementDocuments.fulfilled, (state, action) => {
            state.agreementDocuments = action.payload;
            state.agFetchStatus.agreementDocumentsStatus = "succeeded";
        });
        builder.addCase(fetchAgreementDocuments.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.agFetchStatus.agreementDocumentsStatus = "failed";
        });
        builder.addCase(postAgreementDocument.fulfilled, (state, action) => {
            state.agreementDocuments = [...state.agreementDocuments, action.payload];
        });
        builder.addCase(deleteAgreementDocument.fulfilled, (state, action) => {
            const documentId = action.meta.arg.documentId;
            state.agreementDocuments = [...state.agreementDocuments].filter((doc) => doc.id !== documentId);
            toast.success(`${t("Terms.PDFDeletedSuccessfully")}`);
        });
        builder.addCase(fetchAgreementSigners.pending, (state, _action) => {
            state.agFetchStatus.agreementSignersStatus = "pending";
        });
        builder.addCase(fetchAgreementSigners.fulfilled, (state, action) => {
            state.agreementSigners = action.payload;
            state.agFetchStatus.agreementSignersStatus = "succeeded";
        });
        builder.addCase(fetchAgreementSigners.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.agFetchStatus.agreementSignersStatus = "failed";
        });
        builder.addCase(deleteAgreementsComment.fulfilled, (state, action) => {
            const deletedComment = action.payload.data;
            if (deletedComment.parentId) {
                state.agreementsComments = [...(state.agreementsComments ?? [])].map((comment) => {
                    if (comment.id === deletedComment.parentId) {
                        let thread: BackendComment[] = [];
                        const isAThreadOfTwoComments = comment.thread?.length === 1;
                        const isDeletedComment = comment.thread?.[0].id === deletedComment.commentId;
                        /* Pain to synchronize lexical CommentPlugin with state logic, I didn't found other way yet to make it works
                         ** When there is a thread with 2 comments inside, If I delete one of the comments, visualy it removed both (until a page refresh).
                         ** Except if I replace the thread key value by an array of 1 size length.
                         ** The front end render is done by CommentPlugin
                         */
                        if (isAThreadOfTwoComments && isDeletedComment) {
                            thread = [{}] as any;
                        } else {
                            thread = comment.thread?.filter(
                                (item) => item.id !== deletedComment.commentId,
                            ) as BackendComment[];
                        }
                        return { ...comment, thread };
                    }
                    return comment;
                }) as BackendComment[];
            } else {
                state.agreementsComments = [...(state.agreementsComments ?? [])].filter(
                    (comment) => comment.id !== deletedComment.commentId,
                ) as BackendComment[];
            }
        });
        builder.addCase(deleteAgreementsComment.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(postAgreementsComment.fulfilled, (state, action) => {
            const newComment = action.payload.data;

            if (newComment.parentId) {
                state.agreementsComments = [...(state.agreementsComments ?? [])].map((comment) => {
                    if (comment.id === newComment.parentId) {
                        const thread = [
                            ...(comment?.thread ?? []),
                            { ...newComment, createdAt: Math.floor(new Date(newComment.createdAt).getTime() / 1000) }, // format date to fit with lexical
                        ].filter((item) => item.id !== null) as BackendComment[]; // remove comments with null value that causes front issues
                        return { ...comment, thread };
                    }
                    return comment;
                }) as BackendComment[];
            } else {
                state.agreementsComments = [...(state.agreementsComments ?? []), newComment] as BackendComment[];
            }
        });
        builder.addCase(postAgreementsComment.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(approveAgreement.fulfilled, () => {
            toast.success(`${t("Agreements.ApproveSucceeded")}`);
        });
        builder.addCase(approveAgreement.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(declineAgreement.fulfilled, () => {
            toast.success(`${t("Agreements.DeclineSucceeded")}`);
        });
        builder.addCase(declineAgreement.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        /**
         * Crealo admins only can force send agreement for signature
         */
        builder.addCase(forceSendAgreement.fulfilled, () => {
            toast.success(`Le contrat a bien été envoyé pour signature`);
        });
        builder.addCase(forceSendAgreement.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(sendDocoonAgreement.fulfilled, () => {
            toast.success(`${t("DocoonSignModal.SendSuccess")}`);
        });
        builder.addCase(sendDocoonAgreement.rejected, (_state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "DocoonSignModal.Error" });
        });
        builder.addCase(revertAgreementToDraft.pending, (state) => {
            state.agFetchStatus.revertToDraftStatus = "pending";
        });
        builder.addCase(revertAgreementToDraft.fulfilled, (state, action) => {
            state.agreement = action.payload.agreement;
            state.agreementValidators = action.payload.validators;
            state.agFetchStatus.revertToDraftStatus = "succeeded";
            toast.success(`${t("Agreements.RevertToDraftSucceeded")}`);
        });
        builder.addCase(revertAgreementToDraft.rejected, (state, { payload }) => {
            state.agFetchStatus.revertToDraftStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(putAgreementPublisherSigner.fulfilled, (state, action) => {
            state.agreement = action.payload;
            state.agreementSigners = action.payload.signers;
            toast.success(`${t("Agreements.UpdatePublisherSignerSucceeded")}`);
        });
        builder.addCase(putAgreementPublisherSigner.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchDocoonEvents.pending, (state) => {
            state.docoonEventsFetchStatus = "pending";
        });
        builder.addCase(fetchDocoonEvents.fulfilled, (state, action) => {
            state.docoonEvents = action.payload;
            state.docoonEventsFetchStatus = "succeeded";
        });
        builder.addCase(fetchDocoonEvents.rejected, (state, { payload }) => {
            state.docoonEventsFetchStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "fetchDocoonEvents.Error" });
        });
        builder.addCase(syncDocoonEvents.pending, (state) => {
            state.docoonEventsSyncStatus = "pending";
        });
        builder.addCase(syncDocoonEvents.fulfilled, (state, action) => {
            state.docoonEvents = action.payload;
            state.docoonEventsSyncStatus = "succeeded";
        });
        builder.addCase(syncDocoonEvents.rejected, (state, { payload }) => {
            state.docoonEventsSyncStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "syncDocoonEvents.Error" });
        });
    },
});

export const {
    resetAgreementsTemplateStatus,
    resetAgreementStatus,
    resetAgreementCommentsStatus,
    setSavedEditorContent,
    resetAgreementPDFStatus,
    resetNavigateAfterSendingAg,
    resetAgreement,
    setAgFetchStatus,
    resetDocoonEvents,
} = agreementsSlice.actions;

export default agreementsSlice.reducer;
